/**
 * Experience bootstrap client (FN experience seam, slice 1).
 *
 * Fetches GET /api/v1/experience/bootstrap/ THROUGH the same-origin
 * rewrite (next.config.ts, measured in slice 0: the rewrite forwards the
 * browser's host as X-Forwarded-Host, which is how the backend resolves
 * WHICH experience this hostname serves). Transport goes through
 * sharedApiRequest — the one fence-legal fetch site.
 *
 * The wire contract (ExperienceBootstrapSerializer, five keys, every key
 * always present): the DEFAULT surface is `surface: null` with empty
 * dicts — a legitimate, common state (any unmapped host), NOT an error.
 * The error states are:
 *   - 503: this host IS mapped but its profile is missing/inactive — the
 *     backend refuses to guess (SURFACE_MAPPED_BUT_UNRESOLVABLE). Loud.
 *   - transport failure: unreachable backend. Loud, never a silent
 *     default-that-looks-deliberate.
 * Callers decide presentation; this module only refuses to conflate the
 * three states.
 *
 * feature_gates and catalog are UNCONSTRAINED JSON server-side, so they
 * are sanitized here (the Vite ThemeProvider precedent): unknown shapes
 * degrade to empty, never throw.
 */
import { sharedApiRequest } from "../api/request";
import type { components } from "../api/generated";

type Wire = components["schemas"]["ExperienceBootstrap"];

export interface ExperienceFeatureGates {
  hiddenSteps: readonly string[];
  disabledFeatures: readonly string[];
}

export interface ExperienceBootstrap {
  /** Experience key for this hostname, or null on the default surface. */
  surface: string | null;
  /** Brand display name, or null on the default surface. */
  name: string | null;
  /** Raw served brand tokens (surface_1..link hex + support_email). Kept
   *  verbatim for consumers that want them; NOT painted onto this app's
   *  palette — see applyExperience.ts for the declared decision. */
  branding: Readonly<Record<string, string>>;
  featureGates: ExperienceFeatureGates;
  /** Experience catalog pointer ({ project_type_map }) — consumed by the
   *  catalog read when its owner wires it (slice 3 handoff). */
  catalog: Readonly<Record<string, unknown>>;
}

/** The host is MAPPED but its experience cannot be served (backend 503).
 *  Distinct from the default surface on purpose — rendering the default
 *  brand for a broken mapped host is the silent failure the backend's own
 *  503 exists to prevent. */
export class ExperienceUnavailableError extends Error {
  constructor() {
    super("experience unavailable for this host (mapped but unresolvable)");
    this.name = "ExperienceUnavailableError";
  }
}

const stringList = (value: unknown): readonly string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];

const stringRecord = (value: unknown): Record<string, string> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string",
    ),
  );
};

export function sanitizeBootstrap(raw: Wire): ExperienceBootstrap {
  const gates =
    raw.feature_gates && typeof raw.feature_gates === "object"
      ? (raw.feature_gates as Record<string, unknown>)
      : {};
  return {
    surface: typeof raw.surface === "string" && raw.surface ? raw.surface : null,
    name: typeof raw.name === "string" && raw.name ? raw.name : null,
    branding: stringRecord(raw.branding),
    featureGates: {
      hiddenSteps: stringList(gates.hidden_steps),
      disabledFeatures: stringList(gates.disabled_features),
    },
    catalog:
      raw.catalog && typeof raw.catalog === "object" && !Array.isArray(raw.catalog)
        ? (raw.catalog as Record<string, unknown>)
        : {},
  };
}

export async function fetchExperienceBootstrap(): Promise<ExperienceBootstrap> {
  const response = await sharedApiRequest("/api/v1/experience/bootstrap/", {
    method: "GET",
  });
  if (response.status === 503) throw new ExperienceUnavailableError();
  if (!response.ok) {
    throw new Error(`experience bootstrap failed: ${response.status}`);
  }
  return sanitizeBootstrap((await response.json()) as Wire);
}
